import { TournFlags2 } from '@ps/web-poker-types';
import { is64BitFlagSet } from '@ps/web-poker-utils';
import type { RecentTournament } from '@ps/web-shared-api-types/v0/SwHomeGames';
import type { DeepNonNullable } from 'utility-types';

import { formatIconImg, formatImgMtt, formatTags } from './formatters';
import { formatMttAttr, isPrizePoolRealMoney } from './formatters/attributes';
import { formatGame } from './formatters/game';
import defaultImage from './formatters/imageCash';
import { RPGLink, RPGTypes } from './formatters/types';
import type { Options, PokerGameTile } from './types';
import { getMttState, isCancelled, isRegOpen, isRunning } from './utils/mtt.types';
import { getMttStartTime } from './utils/timeDisplay';

const mapMttType = (
  {
    staticData: {
      tournamentId,
      clubId,
      buyIn,
      currency,
      game,
      maxPlayers,
      isHiLo,
      maxPerTable,
      numReentries,
      rake,
      satelliteTarget,
      speedDisplay,
      server,
      serverObj,
      structure,
      tournGroup,
      tournFlags,
      isPlayMoney,
      minPlayers,
      whenStart,
      whenStartInt,
      regEndAbsInt,
      guaranteedPrizePool,
      scalePM,
      tournFlags2,
      tournFlagsServInt,
      awardScheme,
      timedTournType,
      timedTournPlayersPercent,
      mobileName,
      isRestricted
    },
    dynamicData: { prizePoolToPublish, prizePoolUnit, fakeStatus },
    lastEntry
  }: Omit<DeepNonNullable<RecentTournament>, 'lastEntry'> & Pick<PokerGameTile, 'lastEntry'>,
  options: Options
): PokerGameTile => ({
  id: tournamentId,
  clubId,
  type: RPGTypes.MTT,
  link: RPGLink.TOURN,
  imageUrl: formatImgMtt(
    game,
    buyIn,
    structure,
    Number(isHiLo),
    satelliteTarget,
    maxPerTable,
    tournGroup,
    tournFlags
  ),
  imageUrlV2: formatImgMtt(
    game,
    buyIn,
    structure,
    Number(isHiLo),
    satelliteTarget,
    maxPerTable,
    tournGroup,
    tournFlags,
    true
  ),
  fallbackImageUrl: defaultImage(game, structure, buyIn, 0, Number(isHiLo), 0),
  tags: formatTags({
    game,
    structure,
    minPlayers,
    maxPlayers,
    maxPerTable,
    numReentries,
    satelliteTarget,
    tournGroup,
    tournFlags,
    speedDisplay,
    tournFlags2,
    tournFlagsServInt,
    isMTT: true,
    awardScheme,
    timedTournType,
    timedTournPlayersPercent,
    isHiLo: Boolean(isHiLo)
  }),
  attributes: formatMttAttr(
    {
      buyIn,
      rake,
      maxPlayers,
      currency,
      isPlayMoney: Number(isPlayMoney),
      minPlayers,
      guaranteedPrizePool,
      whenStart: String(whenStart),
      whenStartInt,
      regEndAbsInt,
      scalePM,
      prizePoolToPublish,
      prizePoolUnit,
      fakeStatus,
      winnerAward: undefined
    },
    options
  ),
  lastEntry,
  iconUrl: formatIconImg(RPGTypes.MTT),
  server,
  serverObj,
  mobileName,
  tournGroup,
  hasVideoSupport: is64BitFlagSet(Number(tournFlags2), TournFlags2.TOURNSCRIPT_FLAG2_VIDEO_CHAT),
  tournamentState: getMttState(fakeStatus, isRestricted, tournFlagsServInt)
});

const mapMttTypesFn = (mttTypes: RecentTournament[], options: Options): PokerGameTile[] =>
  // @ts-ignore TODO: Refactor mappers -> Argument of type 'RecentTournament' is not assignable to parameter of type '_DeepNonNullableObject<RecentTournament>'.
  mttTypes.map((type) => mapMttType(type, options));

export const mttUtils = {
  defaultImage,
  formatGame,
  formatImgMtt,
  formatMttAttr,
  formatTags,
  isRegOpen,
  isRunning,
  isCancelled,
  getMttState,
  getMttStartTime,
  isPrizePoolRealMoney
};

export default mapMttTypesFn;
